import { useState, useEffect } from 'react';
import { Search, Bell, Shield, Plus, ToggleLeft, ToggleRight } from 'lucide-react';
import { collection, query, where, getDocs, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { GuardrailRule, GuardrailTrigger } from '../../types/agent';
import { MetricCard, Badge, EmptyState } from '../shared';

export function Guardrails() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState<GuardrailRule[]>([]);
  const [triggers, setTriggers] = useState<GuardrailTrigger[]>([]);

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    if (!user) return;

    try {
      // Load guardrail rules
      const rulesQuery = query(
        collection(db, 'guardrailRules'),
        where('userId', '==', user.uid)
      );
      const rulesSnap = await getDocs(rulesQuery);
      setRules(rulesSnap.docs.map(doc => ({ ...doc.data(), id: doc.id }) as GuardrailRule));

      // Load recent triggers
      const triggersQuery = query(
        collection(db, 'guardrailTriggers'),
        where('userId', '==', user.uid)
      );
      const triggersSnap = await getDocs(triggersQuery);
      setTriggers(triggersSnap.docs.map(doc => ({ ...doc.data(), id: doc.id }) as GuardrailTrigger));

    } catch (error) {
      console.error('Error loading guardrails:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRule = async (ruleId: string, currentEnabled: boolean) => {
    try {
      await updateDoc(doc(db, 'guardrailRules', ruleId), {
        enabled: !currentEnabled,
        updatedAt: Timestamp.now()
      });
      await loadData();
    } catch (error) {
      console.error('Error toggling rule:', error);
      alert('Failed to update rule');
    }
  };

  const activeRules = rules.filter(r => r.enabled).length;
  const totalTriggers = triggers.length;
  const escalations = triggers.filter(t => t.action === 'escalate').length;
  const unresolvedTriggers = triggers.filter(t => !t.resolved).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t3-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <div className="bg-shell-navy border-b border-gray-700 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-gray-400 text-sm">Agent</span>
          <span className="text-gray-600">/</span>
          <span className="text-white text-sm font-medium">Guardrails</span>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search rules..."
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 w-64 focus:outline-none focus:border-t3-primary"
            />
          </div>
          <span className="px-3 py-1 bg-orange-600 text-white text-xs font-semibold rounded uppercase">Growth</span>
          <button className="relative p-2 text-gray-400 hover:text-white">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-8">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <p className="text-xs font-semibold text-t3-primary uppercase tracking-wide mb-1">Kodiac Agent</p>
            <h1 className="text-3xl font-semibold text-gray-900">Guardrails</h1>
            <p className="text-sm text-gray-600 mt-1">Protect your brand voice and enforce content policies</p>
          </div>

          <button
            onClick={() => alert('Create rule functionality coming soon')}
            className="flex items-center gap-2 px-4 py-2 bg-t3-primary text-white rounded-lg hover:bg-purple-700"
          >
            <Plus className="w-4 h-4" />
            Add Guardrail
          </button>
        </div>

        {/* 4-Stat Row */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <MetricCard
            label="Active Rules"
            value={activeRules.toString()}
            
          />
          <MetricCard
            label="Total Triggers"
            value={totalTriggers.toString()}
            
          />
          <MetricCard
            label="Escalations"
            value={escalations.toString()}
            
          />
          <MetricCard
            label="Unresolved"
            value={unresolvedTriggers.toString()}
            
          />
        </div>

        {/* Guardrail Rules */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Guardrail Rules</h2>

          {rules.length === 0 ? (
            <EmptyState
              icon={Shield}
              title="No guardrails configured"
              description="Create guardrails to protect your brand voice and enforce policies"
            />
          ) : (
            <div className="space-y-3">
              {rules.map(rule => (
                <div
                  key={rule.id}
                  className={`flex items-start justify-between p-4 rounded-lg border-2 ${
                    rule.enabled
                      ? 'border-t3-primary bg-purple-50'
                      : 'border-gray-200 bg-gray-50'
                  } transition-all`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-sm font-semibold text-gray-900">{rule.name}</h3>
                      <Badge
                        variant={rule.enabled ? 'success' : 'critical'}
                      >
                        {rule.enabled ? 'Active' : 'Inactive'}
                      </Badge>
                      <Badge variant="primary">P{rule.priority}</Badge>
                      <Badge
                        variant={
                          rule.action === 'block'
                            ? 'critical'
                            : rule.action === 'escalate'
                            ? 'warning'
                            : 'primary'
                        }
                      >
                        {rule.action}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-700 mb-2">{rule.description}</p>

                    {/* Conditions */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-gray-600">Conditions:</span>
                      {rule.conditions.map((condition, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-white rounded border border-gray-200 text-xs text-gray-700"
                        >
                          {condition.type}: {Array.isArray(condition.value) ? condition.value.join(', ') : condition.value}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Toggle */}
                  <button
                    onClick={() => handleToggleRule(rule.id, rule.enabled)}
                    className="ml-4 flex items-center gap-2"
                  >
                    {rule.enabled ? (
                      <ToggleRight className="w-10 h-10 text-t3-primary" />
                    ) : (
                      <ToggleLeft className="w-10 h-10 text-gray-400" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}

          {rules.length === 0 && (
            <div className="mt-6 grid grid-cols-2 gap-4">
              <div className="p-4 bg-surface-warm rounded-lg border border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Profanity Filter</h3>
                <p className="text-xs text-gray-600 mb-3">Block responses containing inappropriate language</p>
                <button className="text-sm text-t3-primary hover:underline">Use Template</button>
              </div>
              <div className="p-4 bg-surface-warm rounded-lg border border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Competitor Mention</h3>
                <p className="text-xs text-gray-600 mb-3">Flag interactions mentioning competitor brands</p>
                <button className="text-sm text-t3-primary hover:underline">Use Template</button>
              </div>
              <div className="p-4 bg-surface-warm rounded-lg border border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Pricing Discussion</h3>
                <p className="text-xs text-gray-600 mb-3">Escalate pricing questions to sales team</p>
                <button className="text-sm text-t3-primary hover:underline">Use Template</button>
              </div>
              <div className="p-4 bg-surface-warm rounded-lg border border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Legal/Compliance</h3>
                <p className="text-xs text-gray-600 mb-3">Block responses to legal or compliance queries</p>
                <button className="text-sm text-t3-primary hover:underline">Use Template</button>
              </div>
            </div>
          )}
        </div>

        {/* Recent Triggers */}
        {triggers.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Recent Triggers</h2>
            <div className="space-y-2">
              {triggers.slice(0, 10).map(trigger => (
                <div
                  key={trigger.id}
                  className="flex items-center justify-between p-3 bg-surface-warm rounded-lg border border-gray-200"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">{trigger.ruleName}</p>
                    <p className="text-xs text-gray-600">
                      {trigger.timestamp.toDate().toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={
                        trigger.action === 'block'
                          ? 'critical'
                          : trigger.action === 'escalate'
                          ? 'warning'
                          : 'primary'
                      }
                    >
                      {trigger.action}
                    </Badge>
                    <Badge variant={trigger.resolved ? 'success' : 'warning'}>
                      {trigger.resolved ? 'Resolved' : 'Pending'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">Guardrail Actions</h3>
          <ul className="space-y-1 text-sm text-blue-800">
            <li>• <strong>Block:</strong> Prevents response from being shown to user</li>
            <li>• <strong>Escalate:</strong> Routes interaction to human review team</li>
            <li>• <strong>Flag:</strong> Marks interaction for review without blocking</li>
            <li>• <strong>Allow with Warning:</strong> Shows response with disclaimer</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
